<script setup lang="ts">
import { computed } from 'vue';
import { Loader, Check } from '@lucide/vue';
import { relativeDateTime } from '@/utilities/Dates';

export type SavedStatusState = 'saving' | 'error' | 'dirty' | 'saved' | 'idle' | 'hidden';

const props = defineProps<{
  status: SavedStatusState;
  updatedAt?: string;
  tick: number;
}>();

const relativeLabel = computed(() => {
  // Touch `tick` so this computed re-evaluates when the parent bumps it.
  void props.tick;
  return relativeDateTime(props.updatedAt) || '';
});
</script>

<template>
  <div
    v-if="status !== 'hidden'"
    class="flex shrink-0 items-center gap-2 text-sm"
    :aria-live="status === 'saving' || status === 'saved' ? 'polite' : 'off'">
    <template v-if="status === 'saving'">
      <Loader class="h-3.5 w-3.5 animate-spin text-slate-500" />
      <span class="text-slate-600">Saving…</span>
    </template>

    <template v-else-if="status === 'error'">
      <span class="relative flex h-2 w-2">
        <span class="relative inline-flex h-2 w-2 rounded-full bg-red-500"></span>
      </span>
      <span class="text-red-600">Save failed</span>
    </template>

    <template v-else-if="status === 'dirty'">
      <span
        class="relative flex h-2 w-2"
        title="Unsaved changes">
        <span class="absolute inline-flex h-full w-full animate-pulse rounded-full bg-amber-400 opacity-30"></span>
        <span class="relative inline-flex h-2 w-2 rounded-full bg-amber-500"></span>
      </span>
      <span class="text-amber-600">Unsaved changes</span>
    </template>

    <template v-else-if="status === 'saved'">
      <Check class="h-3.5 w-3.5 text-green-600" />
      <span class="text-green-600">Saved just now</span>
    </template>

    <template v-else-if="status === 'idle' && relativeLabel">
      <span class="text-slate-500">Last saved {{ relativeLabel }}</span>
    </template>
  </div>
</template>
